import React from "react";
import { Row, Col, Tag, Typography, Table, Button, Empty, message } from "antd";
import { DollarOutlined, PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import THEME from "../theme";
import { formatMoney, formatDate } from "../utils";
import StyledCard from "../components/StyledCard";
import { BASE_URL_BEN } from "../../../../API/apiUrls";
import { PaymentLawyerPdfJs } from "../../export/PaymentLawyerPdfJs";
import { PaymentArLawyerPdfjs } from "../../export/PaymentArLawyerPdfjs";

const { Text } = Typography;

const PaymentTab = ({ judgeData, token }) => {
  const buildPaymentColumns = (printType) => [
    {
      title: "ลำดับ",
      key: "index",
      width: 60,
      align: "center",
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขที่บิล",
      dataIndex: "TMBILL",
      key: "TMBILL",
      render: (val) => <Text style={{ fontFamily: "monospace" }}>{val}</Text>,
    },
    {
      title: "วันที่บิล",
      dataIndex: "TMBILDT",
      key: "TMBILDT",
      render: (val) => formatDate(val),
    },
    {
      title: "ชำระโดย",
      dataIndex: "PAYTYP",
      key: "PAYTYP",
    },
    {
      title: "รหัสชำระ",
      dataIndex: "PAYFOR",
      key: "PAYFOR",
    },
    {
      title: "รายการ",
      dataIndex: "FORDESC",
      key: "FORDESC",
    },
    {
      title: "ชำระงวด",
      dataIndex: "PAYAMT",
      key: "PAYAMT",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "ชำระดอก",
      dataIndex: "PAYAMT_V",
      key: "PAYAMT_V",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "ชำระต้น",
      dataIndex: "PAYAMT_N",
      key: "PAYAMT_N",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "- ส่วนลด",
      dataIndex: "DISCT",
      key: "DISCT",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.danger }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "วันที่ชำระ",
      dataIndex: "PAYDT",
      key: "PAYDT",
      render: (val) => formatDate(val),
    },
    {
      title: "รับสุทธิ",
      dataIndex: "NETPAY",
      key: "NETPAY",
      align: "right",
      render: (val) => (
        <Text strong style={{ color: THEME.success }}>
          {formatMoney(val)} บาท
        </Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "FLAG",
      key: "FLAG",
      width: 40,
      align: "right",
      render: (val) => (
        <Tag color={val === "C" ? "red" : "green"}>
          {val === "C" ? "ยกเลิก" : "รับปกติ"}
        </Tag>
      ),
    },
    {
      title: "ผู้รับ",
      dataIndex: "INPUT_BY",
      key: "INPUT_BY",
      align: "center",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 140,
      render: (_, record) => (
        <Button
          icon={<PrinterOutlined />}
          // disabled={record.FLAG === "C"} // บิลยกเลิกไม่ให้พิมพ์
          onClick={() => handlePrintReceipt(record, printType)}
        >
          พิมพ์ใบเสร็จ
        </Button>
      ),
    },
  ];

  // สร้าง summary รวมทุกคอลัมน์เงิน (ไม่รวมรายการที่ยกเลิก)
  const buildPaymentSummary = (data) => () => {
    const rows = (data || []).filter((r) => r.FLAG !== "C");
    const sumBy = (field) =>
      rows.reduce((sum, r) => sum + Number(r[field] || 0), 0);

    const MoneyCell = ({ value, index, color = THEME.success }) => (
      <Table.Summary.Cell index={index} align="right">
        <Text strong style={{ color }}>
          {formatMoney(value)} บาท
        </Text>
      </Table.Summary.Cell>
    );

    return (
      <Table.Summary.Row style={{ background: THEME.light }}>
        {/* ลำดับ, เลขที่บิล, วันที่บิล, ชำระโดย, รหัสชำระ, รายการ */}
        <Table.Summary.Cell index={0} colSpan={6}>
          <Text strong>รวมทั้งหมด (ไม่รวมยกเลิก)</Text>
        </Table.Summary.Cell>
        <MoneyCell index={6} value={sumBy("PAYAMT")} /> {/* ชำระงวด */}
        <MoneyCell index={7} value={sumBy("PAYAMT_V")} /> {/* ชำระดอก */}
        <MoneyCell index={8} value={sumBy("PAYAMT_N")} /> {/* ชำระต้น */}
        <MoneyCell index={9} value={sumBy("DISCT")} color={THEME.danger} />{" "}
        {/* ส่วนลด */}
        <Table.Summary.Cell index={10} /> {/* วันที่ชำระ */}
        <MoneyCell index={11} value={sumBy("NETPAY")} /> {/* รับสุทธิ */}
        {/* สถานะ, ผู้รับ, การจัดการ */}
        <Table.Summary.Cell index={12} colSpan={3} />
      </Table.Summary.Row>
    );
  };

  // ค่า pagination ใช้ร่วมกัน
  const paymentPagination = {
    defaultPageSize: 5,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  };

  // ---------- ฟังก์ชันพิมพ์ใบเสร็จ (โหลดข้อมูลจาก API แล้วสร้าง PDF ด้วย jsPDF) ----------
  const handlePrintReceipt = async (record, type) => {
    // type: "normal" = chqtran → PaymentLawyerPdfJs
    // type: "ar"     = chqtran_arothr → PaymentArLawyerPdfjs
    if (!record?.TMBILL) return message.warning("ไม่พบใบเสร็จ");

    const isAr = type === "ar";
    const msgKey = isAr ? "load_process_ar_print" : "load_process_print";

    // หน้าเดิมใช้ record.REFDOCNO แต่ข้อมูลใน judgeData ใช้ CONTNO
    const contno = record?.CONTNO || record?.REFDOCNO;

    const url = isAr
      ? `${BASE_URL_BEN}/auth/print-payment-lawyer-ar`
      : `${BASE_URL_BEN}/auth/print-payment-lawyer`;

    const finalPayload = isAr
      ? { TMBILL: record?.TMBILL, CONTNO: contno }
      : { TMBILL: record?.TMBILL, CONTNO: contno, PAYFOR: record?.PAYFOR };

    try {
      message.loading({
        content: "กำลังโหลดข้อมูลใบเสร็จ...",
        key: msgKey,
      });

      const response = await axios.post(url, finalPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data) {
        message.success({
          content: "โหลดข้อมูลสำเร็จ",
          key: msgKey,
        });

        if (isAr) await PaymentArLawyerPdfjs(response.data);
        else await PaymentLawyerPdfJs(response.data);
      } else {
        message.warning({
          content: "ไม่พบข้อมูลใบเสร็จ",
          key: msgKey,
        });
      }
    } catch (error) {
      console.log("error print", error);
      message.error({
        content: "เกิดข้อผิดพลาดในการโหลดใบเสร็จ",
        key: msgKey,
      });
    }
  };

  if (!judgeData?.chqtran?.length && !judgeData?.chqtran_arothr?.length)
    return <Empty description="ไม่มีข้อมูล" />;
  return (
    <>
      {judgeData?.chqtran?.length > 0 && (
        <StyledCard
          title={
            judgeData?.loanNew
              ? "ตารางการรับชำระรวม(ทำยอม)"
              : "ตารางการรับชำระปกติ"
          }
          icon={
            <DollarOutlined
              style={{
                color: judgeData?.loanNew ? THEME.primary : THEME.danger,
                fontSize: 18,
              }}
            />
          }
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={judgeData.chqtran}
            columns={buildPaymentColumns("normal")}
            rowKey="wctId"
            pagination={paymentPagination}
            size="middle"
            scroll={{ x: 1500 }}
            summary={buildPaymentSummary(judgeData?.chqtran)}
          />
        </StyledCard>
      )}

      {judgeData?.chqtran_arothr?.length > 0 && (
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <StyledCard
              title="ตารางการรับลูกหนี้อื่น ๆ"
              icon={
                <DollarOutlined style={{ color: THEME.danger, fontSize: 18 }} />
              }
              style={{ height: "100%" }}
            >
              <Table
                dataSource={judgeData.chqtran_arothr}
                columns={buildPaymentColumns("ar")}
                rowKey="wctId"
                pagination={paymentPagination}
                size="middle"
                scroll={{ x: 1500 }}
                summary={buildPaymentSummary(judgeData?.chqtran_arothr)}
              />
            </StyledCard>
          </Col>
        </Row>
      )}
    </>
  );
};

export default PaymentTab;
